// motion.ts — THE shared motion primitives (design-system-foundation, R58;
// moved from helm-console/src/client into the shared pi-stack client layer by
// Styles x Themes PR-3b, when the ONE modal host landed here and needed the
// same two edges the console dialogs already animated. helm-console/src/
// client/motion.ts is now a re-export of this file, the tooltip/buttons
// precedent). TWO primitives, both built ONCE here because at least two
// downstream seams each would otherwise build their own:
//
//   1. The enter/exit STATE MACHINE, ported from the Helm's find-motion.ts —
//      the only animation code in the repo with a real test (the Bridge smoke
//      lane transpiles it standalone and pins the class sequences). Same
//      four phases, same stale-event immunity, minus the find-bar-specific
//      focus effect (adapters own focus; the machine owns classes/timing).
//   2. A generalised FLIP helper, from the withFlip shape at the Helm's
//      channels.ts (module-private there). jump-back-in (card reorder,
//      R47/R48) and abort-flow (rows closing the gap, R9) both need it and
//      MUST NOT build two.
//
// THE MOTION IDIOM (the `.leaving` exit convention — the Helm's, adopted
// whole): enter mounts the element hidden=false and adds `open` on a
// double-rAF; exit adds `leaving` and finalizes on transitionend with a
// fallback timer (.18s ease transitions, 400ms fallback — the rc-dialog
// figure). prefers-reduced-motion collapses both edges to instant show/hide.
// CSS side: each surface authors its own `.x.leaving { … }` rule INSIDE ITS
// OWN styles.css section anchor and registers it in the reduced-motion guard.
//
// The MACHINE is DOM-free and value-import-free by contract: tests transpile
// this file standalone and drive transitions directly. The FLIP helper and
// prefersReducedMotion() touch browser globals (rects, rAF, matchMedia) —
// they are adapters over the same convention, not part of the machine.

export type MotionPhase = 'closed' | 'entering' | 'open' | 'leaving';

/** 'open'/'close' come from the caller; 'settle' is the double-rAF tick that
 * ends an enter; 'hide-done' is transitionend (or the fallback timer) after
 * a leave. */
export type MotionEvent = 'open' | 'close' | 'settle' | 'hide-done';

export const MOTION_OPEN_CLASS = 'open';
export const MOTION_LEAVING_CLASS = 'leaving';
/** Fallback for a browser that never fires transitionend — the rc-dialog
 * figure (transitions are .18s; the timer only matters when the event is
 * lost). */
export const MOTION_LEAVE_FALLBACK_MS = 400;

export interface MotionFx {
  /** When present, set [hidden] to this value NOW. */
  hidden?: boolean;
  /** Classes to add / remove now. */
  add: string[];
  remove: string[];
  /** Enter path: double-rAF, then feed 'settle'. */
  scheduleSettle: boolean;
  /** Leave path: transitionend + fallback timer, then feed 'hide-done'. */
  scheduleHide: boolean;
}

const NO_FX: MotionFx = { add: [], remove: [], scheduleSettle: false, scheduleHide: false };

/** The transition function. Stale scheduled events are harmless by
 * construction — every event only acts in the phase that scheduled it. */
export function motionNext(
  phase: MotionPhase,
  ev: MotionEvent,
  reducedMotion: boolean,
): { phase: MotionPhase; fx: MotionFx } {
  switch (ev) {
    case 'open': {
      if (phase === 'entering' || phase === 'open') {
        // Already showing — an open gesture is a no-op for the machine
        // (adapters may still refocus, their concern).
        return { phase, fx: NO_FX };
      }
      if (reducedMotion) {
        // Instant show: visible and settled in one step, nothing scheduled.
        return {
          phase: 'open',
          fx: { ...NO_FX, hidden: false, add: [MOTION_OPEN_CLASS], remove: [MOTION_LEAVING_CLASS] },
        };
      }
      // From closed (fresh enter) or leaving (reopen mid-fade — the
      // transition reverses from wherever opacity is; never a hidden flash).
      return {
        phase: 'entering',
        fx: {
          ...NO_FX,
          hidden: false,
          remove: [MOTION_OPEN_CLASS, MOTION_LEAVING_CLASS],
          scheduleSettle: true,
        },
      };
    }
    case 'settle': {
      // Only the enter that scheduled it may settle — a settle landing after
      // an Esc-during-enter (phase already 'leaving') is a stale tick.
      if (phase !== 'entering') return { phase, fx: NO_FX };
      return { phase: 'open', fx: { ...NO_FX, add: [MOTION_OPEN_CLASS] } };
    }
    case 'close': {
      if (phase === 'closed' || phase === 'leaving') return { phase, fx: NO_FX };
      if (reducedMotion) {
        // Instant hide.
        return {
          phase: 'closed',
          fx: { ...NO_FX, hidden: true, remove: [MOTION_OPEN_CLASS, MOTION_LEAVING_CLASS] },
        };
      }
      // From 'open' — or from 'entering': a close during the enter animation
      // leaves cleanly from wherever the transition got to.
      return {
        phase: 'leaving',
        fx: { ...NO_FX, remove: [MOTION_OPEN_CLASS], add: [MOTION_LEAVING_CLASS], scheduleHide: true },
      };
    }
    case 'hide-done': {
      // Only a leave in progress may hide — a fallback timer or transitionend
      // arriving after a reopen is stale.
      if (phase !== 'leaving') return { phase, fx: NO_FX };
      return { phase: 'closed', fx: { ...NO_FX, hidden: true, remove: [MOTION_LEAVING_CLASS] } };
    }
  }
}

/** One read, shared so every adapter asks the same question the same way. */
export function prefersReducedMotion(): boolean {
  return typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;
}

// ---------------------------------------------------------------------
// FLIP — First/Last/Invert/Play over a keyed container re-render.
// ---------------------------------------------------------------------

export interface FlipOptions {
  /** The attribute carrying the stable per-item key, e.g. 'data-rel'. */
  keyAttr: string;
  /** Class added to items that were NOT present before the render (their
   * entrance is the seam's own CSS, registered in the reduced-motion guard). */
  enterClass?: string;
  /** Class carried by survivors WHILE they play back to their new position
   * (added at release, removed on transitionend with the shared fallback
   * timer). This is where a seam hangs its travel transition — e.g. a
   * deliberate-duration transform — without slowing the element's ordinary
   * hover/press transitions. */
  moveClass?: string;
  /** Movement below this many px is not animated (default 2 — sub-pixel
   * churn reads as jitter, not motion). */
  minDelta?: number;
}

/**
 * Re-render a keyed list, animating survivors from their old positions.
 *
 * Measures every `[keyAttr]` child's rect BEFORE render, runs the caller's
 * render (innerHTML rebuild, reorder, removal — anything), then plays each
 * survivor from its inverted offset on the next frame. BOTH AXES: the first
 * consumer (jump-back-in's card strip) reorders HORIZONTALLY within a grid
 * row, where a top-only measurement reads delta 0 and animates nothing —
 * abort-flow's vertical row-gap close is the dy-dominant case of the same
 * translate. New items get `enterClass` instead of a translate. Honours
 * reduced motion by just rendering — callers never need their own
 * matchMedia guard.
 */
export function withFlip(container: HTMLElement, render: () => void, opts: FlipOptions): void {
  if (prefersReducedMotion()) {
    render();
    return;
  }
  const sel = `[${opts.keyAttr}]`;
  const before = new Map<string, { top: number; left: number }>();
  for (const el of container.querySelectorAll<HTMLElement>(sel)) {
    const k = el.getAttribute(opts.keyAttr);
    if (k !== null) {
      const r = el.getBoundingClientRect();
      before.set(k, { top: r.top, left: r.left });
    }
  }
  render();
  const min = opts.minDelta ?? 2;
  const moved: HTMLElement[] = [];
  for (const el of container.querySelectorAll<HTMLElement>(sel)) {
    const k = el.getAttribute(opts.keyAttr);
    const prev = k === null ? undefined : before.get(k);
    if (prev === undefined) {
      if (opts.enterClass) el.classList.add(opts.enterClass);
      continue;
    }
    const now = el.getBoundingClientRect();
    const dx = prev.left - now.left;
    const dy = prev.top - now.top;
    if (Math.abs(dx) < min && Math.abs(dy) < min) continue;
    el.style.transform = `translate(${dx}px, ${dy}px)`;
    el.style.transition = 'none';
    moved.push(el);
  }
  if (moved.length)
    requestAnimationFrame(() => {
      for (const el of moved) {
        if (opts.moveClass) {
          const cls = opts.moveClass;
          el.classList.add(cls);
          const done = (): void => el.classList.remove(cls);
          el.addEventListener('transitionend', done, { once: true });
          // Same figure as the leave fallback: the class must not stick on a
          // browser that never fires transitionend.
          setTimeout(done, MOTION_LEAVE_FALLBACK_MS);
        }
        el.style.transform = '';
        el.style.transition = '';
      }
    });
}
